import {
  useQuery
} from "@tanstack/react-query";

import {
  Clock3,
  IndianRupee,
  LoaderCircle,
  Mail,
  MessageCircle,
  Phone,
  Search,
  ShoppingBag,
  UserRound,
  Users,
  X
} from "lucide-react";

import {
  useState
} from "react";

import {
  getAdminCustomers
} from "../services/admin";

import type {
  AdminCustomer
} from "../types/admin";

function money(
  value: number
) {
  return Number(
    value
  ).toLocaleString(
    "en-IN"
  );
}

function whatsappUrl(
  customer:
    AdminCustomer
) {
  const number =
    (
      customer.whatsappNumber ||
      customer.phone
    ).replace(
      /\D/g,
      ""
    );

  const finalNumber =
    number.startsWith(
      "91"
    )
      ? number
      : `91${number}`;

  const message =
    encodeURIComponent(
      `Hello ${customer.customerName}, this is RiNi Bodycare.`
    );

  return (
    `https://wa.me/${finalNumber}?text=${message}`
  );
}

export function AdminCustomersPage() {
  const [search, setSearch] =
    useState("");

  const [
    selectedCustomer,
    setSelectedCustomer
  ] =
    useState<
      AdminCustomer |
      null
    >(null);

  const customerQuery =
    useQuery({
      queryKey: [
        "admin",
        "customers",
        search
      ],

      queryFn: () =>
        getAdminCustomers(
          search.trim() ||
          undefined
        )
    });

  const customers =
    customerQuery.data
      ?.customers ??
    [];

  const summary =
    customerQuery.data
      ?.summary;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#70845b]">
          Customer relationships
        </p>

        <h1 className="mt-2 font-serif text-4xl text-[#38251e]">
          Customers
        </h1>

        <p className="mt-3 max-w-2xl text-[#746d65]">
          Review customer history,
          lifetime value, repeat purchases
          and contact information.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl bg-white p-5">
          <Users
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {
              summary
                ?.totalCustomers ??
              0
            }
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Customers
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <UserRound
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {
              summary
                ?.newThisMonth ??
              0
            }
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            New this month
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <ShoppingBag
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {
              summary
                ?.repeatCustomers ??
              0
            }
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Repeat customers
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <IndianRupee
            size={22}
            className="text-[#a27a4d]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            ₹
            {money(
              summary
                ?.lifetimeRevenue ??
              0
            )}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Lifetime revenue
          </p>
        </div>
      </div>

      <div className="rounded-[2rem] bg-white p-5">
        <label className="relative block">
          <Search
            size={18}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#746d65]"
          />

          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Search customer, phone, email or order"
            className="w-full rounded-xl border border-[#5e3b2c]/15 bg-[#fffdf8] py-3 pl-11 pr-4 text-sm"
          />
        </label>
      </div>

      {customerQuery.isLoading && (
        <div className="flex min-h-60 items-center justify-center">
          <LoaderCircle
            size={36}
            className="animate-spin text-[#70845b]"
          />
        </div>
      )}

      {!customerQuery.isLoading && (
        <div className="overflow-hidden rounded-[2rem] bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-[#5e3b2c]/10 bg-[#fffaf4] text-left text-xs uppercase tracking-[0.12em] text-[#70845b]">
                  <th className="px-6 py-4">
                    Customer
                  </th>

                  <th className="px-6 py-4 text-center">
                    Orders
                  </th>

                  <th className="px-6 py-4">
                    Lifetime Spend
                  </th>

                  <th className="px-6 py-4">
                    Last Order
                  </th>

                  <th className="px-6 py-4">
                    Payment
                  </th>

                  <th className="px-6 py-4 text-right">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {customers.map(
                  (customer) => (
                    <tr
                      key={
                        customer.id
                      }
                      className="border-b border-[#5e3b2c]/5"
                    >
                      <td className="px-6 py-5">
                        <p className="font-semibold text-[#38251e]">
                          {
                            customer.customerName
                          }
                        </p>

                        <p className="mt-1 text-sm text-[#746d65]">
                          {
                            customer.phone
                          }
                        </p>

                        {customer.email && (
                          <p className="mt-1 text-xs text-[#746d65]">
                            {
                              customer.email
                            }
                          </p>
                        )}
                      </td>

                      <td className="px-6 py-5 text-center text-lg font-semibold text-[#38251e]">
                        {
                          customer.totalOrders
                        }
                      </td>

                      <td className="px-6 py-5 font-semibold text-[#5e3b2c]">
                        ₹
                        {money(
                          customer.totalSpent
                        )}
                      </td>

                      <td className="px-6 py-5 text-sm text-[#746d65]">
                        {new Date(
                          customer.lastOrderDate
                        ).toLocaleDateString(
                          "en-IN"
                        )}
                      </td>

                      <td className="px-6 py-5">
                        {customer.pendingOrders >
                        0 ? (
                          <span className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
                            {
                              customer.pendingOrders
                            } pending
                          </span>
                        ) : (
                          <span className="rounded-full bg-[#edf1e8] px-3 py-1.5 text-xs font-semibold text-[#526746]">
                            Up to date
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedCustomer(
                                customer
                              )
                            }
                            className="rounded-full border border-[#5e3b2c]/15 px-4 py-2 text-xs font-semibold text-[#5e3b2c]"
                          >
                            View profile
                          </button>

                          <a
                            href={
                              whatsappUrl(
                                customer
                              )
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-full bg-[#edf1e8] px-4 py-2 text-xs font-semibold text-[#526746]"
                          >
                            <MessageCircle
                              size={15}
                            />

                            WhatsApp
                          </a>
                        </div>
                      </td>
                    </tr>
                  )
                )}

                {customers.length ===
                  0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-16 text-center text-[#746d65]"
                    >
                      No customers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedCustomer && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-6 w-full max-w-5xl rounded-[2rem] bg-[#f8f4ee] shadow-2xl">
            <div className="sticky top-0 z-10 flex items-start justify-between border-b border-[#5e3b2c]/10 bg-[#f8f4ee] p-7">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#70845b]">
                  Customer profile
                </p>

                <h2 className="mt-2 font-serif text-4xl text-[#38251e]">
                  {
                    selectedCustomer
                      .customerName
                  }
                </h2>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedCustomer(
                    null
                  )
                }
                className="rounded-full bg-white p-3 shadow-sm"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6 p-7">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="rounded-2xl bg-white p-5">
                  <ShoppingBag
                    size={20}
                    className="text-[#70845b]"
                  />

                  <p className="mt-3 text-2xl font-semibold">
                    {
                      selectedCustomer
                        .totalOrders
                    }
                  </p>

                  <p className="text-sm text-[#746d65]">
                    Orders
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-5">
                  <IndianRupee
                    size={20}
                    className="text-[#70845b]"
                  />

                  <p className="mt-3 text-2xl font-semibold">
                    ₹
                    {money(
                      selectedCustomer
                        .totalSpent
                    )}
                  </p>

                  <p className="text-sm text-[#746d65]">
                    Lifetime spend
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-5">
                  <Clock3
                    size={20}
                    className="text-[#70845b]"
                  />

                  <p className="mt-3 text-2xl font-semibold">
                    {
                      selectedCustomer
                        .pendingOrders
                    }
                  </p>

                  <p className="text-sm text-[#746d65]">
                    Pending
                  </p>
                </div>

                <div className="rounded-2xl bg-white p-5">
                  <UserRound
                    size={20}
                    className="text-[#70845b]"
                  />

                  <p className="mt-3 text-2xl font-semibold">
                    {
                      selectedCustomer
                        .completedOrders
                    }
                  </p>

                  <p className="text-sm text-[#746d65]">
                    Completed
                  </p>
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                <section className="rounded-2xl bg-white p-6">
                  <h3 className="font-serif text-2xl text-[#38251e]">
                    Contact
                  </h3>

                  <div className="mt-5 space-y-3 text-sm">
                    <p className="flex items-center gap-3">
                      <Phone
                        size={17}
                      />

                      {
                        selectedCustomer.phone
                      }
                    </p>

                    {selectedCustomer.email && (
                      <p className="flex items-center gap-3">
                        <Mail
                          size={17}
                        />

                        {
                          selectedCustomer.email
                        }
                      </p>
                    )}

                    <a
                      href={
                        whatsappUrl(
                          selectedCustomer
                        )
                      }
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-[#edf1e8] px-4 py-2 font-semibold text-[#526746]"
                    >
                      <MessageCircle
                        size={16}
                      />

                      WhatsApp customer
                    </a>
                  </div>
                </section>

                <section className="rounded-2xl bg-white p-6">
                  <h3 className="font-serif text-2xl text-[#38251e]">
                    Delivery addresses
                  </h3>

                  <div className="mt-5 space-y-4">
                    {selectedCustomer
                      .addresses
                      .map(
                        (
                          address,
                          index
                        ) => (
                          <div
                            key={
                              `${address.postalCode}-${index}`
                            }
                            className="border-b border-[#5e3b2c]/10 pb-4 text-sm leading-6 text-[#746d65] last:border-0"
                          >
                            <p>
                              {
                                address.addressLine1
                              }
                            </p>

                            {address.addressLine2 && (
                              <p>
                                {
                                  address.addressLine2
                                }
                              </p>
                            )}

                            <p>
                              {
                                address.city
                              },{" "}
                              {
                                address.state
                              }
                            </p>

                            <p>
                              PIN:{" "}
                              {
                                address.postalCode
                              }
                            </p>
                          </div>
                        )
                      )}
                  </div>
                </section>
              </div>

              <section className="rounded-2xl bg-white p-6">
                <h3 className="font-serif text-2xl text-[#38251e]">
                  Products purchased
                </h3>

                <div className="mt-5 grid gap-3 md:grid-cols-2">
                  {selectedCustomer
                    .productsPurchased
                    .map(
                      (product) => (
                        <div
                          key={
                            product.sku
                          }
                          className="flex items-center justify-between rounded-xl bg-[#fffaf4] p-4"
                        >
                          <div>
                            <p className="font-semibold text-[#38251e]">
                              {
                                product.productName
                              }
                            </p>

                            <p className="mt-1 text-xs text-[#746d65]">
                              {
                                product.sku
                              }
                            </p>
                          </div>

                          <span className="font-semibold text-[#5e3b2c]">
                            Qty{" "}
                            {
                              product.quantity
                            }
                          </span>
                        </div>
                      )
                    )}
                </div>
              </section>

              <section className="rounded-2xl bg-white p-6">
                <h3 className="font-serif text-2xl text-[#38251e]">
                  Recent orders
                </h3>

                <div className="mt-5 overflow-x-auto">
                  <table className="w-full min-w-[650px]">
                    <thead>
                      <tr className="border-b border-[#5e3b2c]/10 text-left text-xs uppercase tracking-[0.1em] text-[#70845b]">
                        <th className="pb-3">
                          Order
                        </th>

                        <th className="pb-3">
                          Date
                        </th>

                        <th className="pb-3">
                          Status
                        </th>

                        <th className="pb-3">
                          Payment
                        </th>

                        <th className="pb-3 text-right">
                          Total
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {selectedCustomer
                        .recentOrders
                        .map(
                          (order) => (
                            <tr
                              key={
                                order.id
                              }
                              className="border-b border-[#5e3b2c]/5 text-sm"
                            >
                              <td className="py-4 font-semibold">
                                {
                                  order.orderNumber
                                }
                              </td>

                              <td className="py-4 text-[#746d65]">
                                {new Date(
                                  order.createdAt
                                ).toLocaleDateString(
                                  "en-IN"
                                )}
                              </td>

                              <td className="py-4">
                                {
                                  order.orderStatus
                                }
                              </td>

                              <td className="py-4">
                                {
                                  order.paymentStatus
                                }
                              </td>

                              <td className="py-4 text-right font-semibold">
                                ₹
                                {money(
                                  order.totalAmount
                                )}
                              </td>
                            </tr>
                          )
                        )}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
